import { gql } from '@apollo/client';
import { LocaleCode, LOCALE_BY_CODE } from '../../../../../utils/Locale';
import { queryWithFallbackKey } from '../../../common/tools/queryWithFallbackKey';

export const GET_SITE_CONTENT_ELEMENT_LISTING_QUERY = queryWithFallbackKey(
    gql`
        fragment ExternalLinkElementFieldCollectionFields on fieldcollection_ExternalLinkElement {
            __typename
            linkText
            linkUrl
        }
        fragment InternalLinkElementFieldCollectionFields on fieldcollection_InternalLinkElement {
            __typename
            linkText
            linkRoute
        }
        fragment ResourceLibraryLinkElementFieldCollectionFields on fieldcollection_ResourceLibraryItemLinkElement {
            __typename
            linkText
            resourceLibraryItem {
                ... on object_ResourceLibraryItem {
                    id
                    name
                }
                ... on object_ResourceLibraryArticleItem {
                    id
                    name
                }
                ... on object_ResourceLibraryNewsItem {
                    id
                    name
                }
                ... on object_ResourceLibraryNewProductIntroductionItem {
                    id
                    name
                }
                ... on object_ResourceLibrarySitePageItem {
                    id
                    name
                }
            }
        }
        fragment ContentSeciontElementFieldCollectionFields on fieldcollection_ContentSectionElement {
            __typename
            title
            content
        }
        fragment ExternalLinkFields on object_ExternalLinkElement {
            __typename
            key
            linkText
            linkUrl
        }
        fragment ExternalLinkFieldsOptional on object_ExternalLinkElementOptional {
            __typename
            key
            linkText
            linkUrl
        }
        fragment InternalLinkFields on object_InternalLinkElement {
            __typename
            key
            linkText
            linkRoute
        }
        fragment InternalLinkFieldsOptional on object_InternalLinkElementOptional {
            __typename
            key
            linkText
            linkRoute
        }
        fragment LinkCollectionFields on object_LinkCollectionElement {
            __typename
            key
            links {
                ...InternalLinkElementFieldCollectionFields
                ...ExternalLinkElementFieldCollectionFields
            }
        }
        fragment ExternalImageLinkElementFieldCollectionFields on fieldcollection_ExternalImageLinkElement {
            __typename
            tooltip
            linkUrl
            image {
                fullpath
                metadata {
                    name
                    data
                    type
                }
            }
        }
        fragment InternalImageLinkElementFieldCollectionFields on fieldcollection_InternalImageLinkElement {
            __typename
            tooltip
            linkRoute
            image {
                fullpath
                metadata {
                    name
                    data
                    type
                }
            }
        }
        fragment ImageLinkCollectionFields on object_ImageLinkCollectionElement {
            __typename
            key
            links {
                ...InternalImageLinkElementFieldCollectionFields
                ...ExternalImageLinkElementFieldCollectionFields
            }
        }
        fragment ImageElementFields on object_ImageElement {
            __typename
            key
            image {
                fullpath
            }
        }
        fragment ImageElementFieldsOptional on object_ImageElementOptional {
            __typename
            key
            image {
                fullpath
            }
        }
        fragment PlainTextElementFields on object_PlainTextElement {
            __typename
            key
            content
        }
        fragment PlainTextElementFieldsOptional on object_PlainTextElementOptional {
            __typename
            key
            content
        }
        fragment PlainMultilineTextElementFields on object_PlainMultilineTextElement {
            __typename
            key
            content
        }
        fragment PlainMultilineTextElementFieldsOptional on object_PlainMultilineTextElementOptional {
            __typename
            key
            content
        }
        fragment RichTextElementFields on object_RichTextElement {
            __typename
            key
            content
        }
        fragment RichTextElementFieldsOptional on object_RichTextElementOptional {
            __typename
            key
            content
        }
        fragment TableElementFields on object_TableElement {
            __typename
            key
            tableHeader
            tableBody
        }
        fragment PartitionedTableElementFields on object_PartitionedTableElement {
            __typename
            key
            commonTableHeader
            tablePartitions {
                ... on fieldcollection_PartitionTableEntry {
                    selectionKey
                    tableHeader
                    tableBody
                }
            }
        }
        fragment InternalLinkSectionElementFields on object_InternalLinkSectionElement {
            __typename
            key
            title
            text
            links {
                ...InternalLinkElementFieldCollectionFields
                ...ResourceLibraryLinkElementFieldCollectionFields
            }
        }
        fragment HighlightsCollectionElementFields on object_HighlightsCollectionElement {
            __typename
            key
            entries {
                ... on fieldcollection_HighlightsCollectionEntry {
                    title
                    image {
                        fullpath
                    }
                    text
                }
            }
        }
        fragment GovernanceRoleElementFields on object_GovernanceRoleElement {
            __typename
            key
            title
            personProfiles {
                ... on fieldcollection_PersonProfileElement {
                    name
                    content
                }
            }
        }
        fragment ProductApplicationSegmentCardCollectionElementFields on object_ProductApplicationSegmentCardCollectionElement {
            __typename
            key
            entries {
                ... on fieldcollection_ProductApplicationSegmentCard {
                    image {
                        fullpath
                    }
                    productApplicationSegment {
                        ... on object_ProductApplicationSegment {
                            name
                        }
                    }
                    route
                }
            }
        }
        fragment BrandCardCollectionElementFields on object_BrandCardCollectionElement {
            __typename
            key
            entries {
                ... on fieldcollection_BrandCard {
                    brand {
                        ... on object_Brand {
                            id
                            name
                            logo {
                                fullpath
                            }
                        }
                    }
                    text
                    externalLinkUrl
                }
            }
        }
        fragment CompanyFocusTopicElementFields on object_CompanyFocusTopicElement {
            __typename
            key
            title
            entries {
                ...ContentSeciontElementFieldCollectionFields
                ...ExternalLinkElementFieldCollectionFields
                ...InternalLinkElementFieldCollectionFields
            }
        }
        fragment ElementReferenceFields on object_ElementReference {
            __typename
            key
            reference {
                ...ExternalLinkFields
                ...ExternalLinkFieldsOptional
                ...InternalLinkFields
                ...InternalLinkFieldsOptional
                ...LinkCollectionFields
                ...ImageLinkCollectionFields
                ...ImageElementFields
                ...ImageElementFieldsOptional
                ...PlainTextElementFields
                ...PlainTextElementFieldsOptional
                ...PlainMultilineTextElementFields
                ...PlainMultilineTextElementFieldsOptional
                ...RichTextElementFields
                ...RichTextElementFieldsOptional
                ...TableElementFields
                ...PartitionedTableElementFields
                ...HighlightsCollectionElementFields
                ...GovernanceRoleElementFields
                ...ProductApplicationSegmentCardCollectionElementFields
                ...BrandCardCollectionElementFields
                ...CompanyFocusTopicElementFields
                ...ElementReferenceNestedFields
            }
        }
        fragment ElementReferenceNestedFields on object_ElementReference {
            __typename
            key
            reference {
                ...ExternalLinkFields
                ...ExternalLinkFieldsOptional
                ...InternalLinkFields
                ...InternalLinkFieldsOptional
                ...LinkCollectionFields
                ...ImageLinkCollectionFields
                ...ImageElementFields
                ...ImageElementFieldsOptional
                ...PlainTextElementFields
                ...PlainTextElementFieldsOptional
                ...PlainMultilineTextElementFields
                ...PlainMultilineTextElementFieldsOptional
                ...RichTextElementFields
                ...RichTextElementFieldsOptional
                ...TableElementFields
                ...PartitionedTableElementFields
                ...HighlightsCollectionElementFields
                ...GovernanceRoleElementFields
                ...ProductApplicationSegmentCardCollectionElementFields
                ...BrandCardCollectionElementFields
                ...CompanyFocusTopicElementFields
            }
        }
        fragment InfoBannerCollectionEntryFieldCollectionFields on fieldcollection_InfoBannerCollectionEntry {
            __typename
            identifier
            enabled
            content
        }
        fragment InfoBannerCollectionElementFields on object_InfoBannerCollectionElement {
            __typename
            id
            key
            entries {
                ...InfoBannerCollectionEntryFieldCollectionFields
            }
        }

        query getSiteContentElementListing($defaultLanguage: String!, $fullpath: String!) {
            getObjectFolder(defaultLanguage: $defaultLanguage, fullpath: $fullpath) {
                children {
                    ...ExternalLinkFields
                    ...ExternalLinkFieldsOptional
                    ...InternalLinkFields
                    ...InternalLinkFieldsOptional
                    ...LinkCollectionFields
                    ...ImageLinkCollectionFields
                    ...ImageElementFields
                    ...ImageElementFieldsOptional
                    ...PlainTextElementFields
                    ...PlainTextElementFieldsOptional
                    ...PlainMultilineTextElementFields
                    ...PlainMultilineTextElementFieldsOptional
                    ...RichTextElementFields
                    ...RichTextElementFieldsOptional
                    ...TableElementFields
                    ...PartitionedTableElementFields
                    ...HighlightsCollectionElementFields
                    ...GovernanceRoleElementFields
                    ...ProductApplicationSegmentCardCollectionElementFields
                    ...BrandCardCollectionElementFields
                    ...CompanyFocusTopicElementFields
                    ...InfoBannerCollectionElementFields
                    ...ElementReferenceFields
                    ...InternalLinkSectionElementFields
                    ... on object_folder {
                        __typename
                        key
                        fullpath
                        # Level 2
                        children {
                            ...ExternalLinkFields
                            ...ExternalLinkFieldsOptional
                            ...InternalLinkFields
                            ...InternalLinkFieldsOptional
                            ...LinkCollectionFields
                            ...ImageElementFields
                            ...ImageElementFieldsOptional
                            ...ImageLinkCollectionFields
                            ...PlainTextElementFields
                            ...PlainTextElementFieldsOptional
                            ...PlainMultilineTextElementFields
                            ...PlainMultilineTextElementFieldsOptional
                            ...RichTextElementFields
                            ...RichTextElementFieldsOptional
                            ...TableElementFields
                            ...PartitionedTableElementFields
                            ...HighlightsCollectionElementFields
                            ...GovernanceRoleElementFields
                            ...ProductApplicationSegmentCardCollectionElementFields
                            ...BrandCardCollectionElementFields
                            ...CompanyFocusTopicElementFields
                            ...InfoBannerCollectionElementFields
                            ...ElementReferenceFields
                            ...InternalLinkSectionElementFields
                            ... on object_folder {
                                __typename
                                key
                                fullpath
                                # Level 3
                                children {
                                    ...ExternalLinkFields
                                    ...ExternalLinkFieldsOptional
                                    ...InternalLinkFields
                                    ...InternalLinkFieldsOptional
                                    ...LinkCollectionFields
                                    ...ImageLinkCollectionFields
                                    ...ImageElementFields
                                    ...ImageElementFieldsOptional
                                    ...PlainTextElementFields
                                    ...PlainTextElementFieldsOptional
                                    ...PlainMultilineTextElementFields
                                    ...PlainMultilineTextElementFieldsOptional
                                    ...RichTextElementFields
                                    ...RichTextElementFieldsOptional
                                    ...TableElementFields
                                    ...PartitionedTableElementFields
                                    ...HighlightsCollectionElementFields
                                    ...GovernanceRoleElementFields
                                    ...ProductApplicationSegmentCardCollectionElementFields
                                    ...BrandCardCollectionElementFields
                                    ...CompanyFocusTopicElementFields
                                    ...InfoBannerCollectionElementFields
                                    ...ElementReferenceFields
                                    ...InternalLinkSectionElementFields
                                    ... on object_folder {
                                        __typename
                                        key
                                        fullpath
                                        # Level 4
                                        children {
                                            ...ExternalLinkFields
                                            ...ExternalLinkFieldsOptional
                                            ...InternalLinkFields
                                            ...InternalLinkFieldsOptional
                                            ...LinkCollectionFields
                                            ...ImageLinkCollectionFields
                                            ...ImageElementFields
                                            ...ImageElementFieldsOptional
                                            ...PlainTextElementFields
                                            ...PlainTextElementFieldsOptional
                                            ...PlainMultilineTextElementFields
                                            ...PlainMultilineTextElementFieldsOptional
                                            ...RichTextElementFields
                                            ...RichTextElementFieldsOptional
                                            ...TableElementFields
                                            ...PartitionedTableElementFields
                                            ...HighlightsCollectionElementFields
                                            ...GovernanceRoleElementFields
                                            ...ProductApplicationSegmentCardCollectionElementFields
                                            ...BrandCardCollectionElementFields
                                            ...CompanyFocusTopicElementFields
                                            ...InfoBannerCollectionElementFields
                                            ...ElementReferenceFields
                                            ...InternalLinkSectionElementFields
                                            ... on object_folder {
                                                __typename
                                                key
                                                fullpath
                                                # Level 5
                                                children {
                                                    ...ExternalLinkFields
                                                    ...ExternalLinkFieldsOptional
                                                    ...InternalLinkFields
                                                    ...InternalLinkFieldsOptional
                                                    ...LinkCollectionFields
                                                    ...ImageLinkCollectionFields
                                                    ...ImageElementFields
                                                    ...ImageElementFieldsOptional
                                                    ...PlainTextElementFields
                                                    ...PlainTextElementFieldsOptional
                                                    ...PlainMultilineTextElementFields
                                                    ...PlainMultilineTextElementFieldsOptional
                                                    ...RichTextElementFields
                                                    ...RichTextElementFieldsOptional
                                                    ...TableElementFields
                                                    ...PartitionedTableElementFields
                                                    ...HighlightsCollectionElementFields
                                                    ...GovernanceRoleElementFields
                                                    ...ProductApplicationSegmentCardCollectionElementFields
                                                    ...BrandCardCollectionElementFields
                                                    ...CompanyFocusTopicElementFields
                                                    ...InfoBannerCollectionElementFields
                                                    ...ElementReferenceFields
                                                    ...InternalLinkSectionElementFields
                                                    ... on object_folder {
                                                        __typename
                                                        key
                                                        fullpath
                                                        # Level 6
                                                        children {
                                                            ...ExternalLinkFields
                                                            ...ExternalLinkFieldsOptional
                                                            ...InternalLinkFields
                                                            ...InternalLinkFieldsOptional
                                                            ...LinkCollectionFields
                                                            ...ImageLinkCollectionFields
                                                            ...ImageElementFields
                                                            ...ImageElementFieldsOptional
                                                            ...PlainTextElementFields
                                                            ...PlainTextElementFieldsOptional
                                                            ...PlainMultilineTextElementFields
                                                            ...PlainMultilineTextElementFieldsOptional
                                                            ...RichTextElementFields
                                                            ...RichTextElementFieldsOptional
                                                            ...TableElementFields
                                                            ...PartitionedTableElementFields
                                                            ...HighlightsCollectionElementFields
                                                            ...GovernanceRoleElementFields
                                                            ...ProductApplicationSegmentCardCollectionElementFields
                                                            ...BrandCardCollectionElementFields
                                                            ...CompanyFocusTopicElementFields
                                                            ...InfoBannerCollectionElementFields
                                                            ...ElementReferenceFields
                                                            ...InternalLinkSectionElementFields
                                                            ... on object_folder {
                                                                __typename
                                                                key
                                                                fullpath
                                                                # Level 7
                                                                children {
                                                                    ...ExternalLinkFields
                                                                    ...ExternalLinkFieldsOptional
                                                                    ...InternalLinkFields
                                                                    ...InternalLinkFieldsOptional
                                                                    ...LinkCollectionFields
                                                                    ...ImageLinkCollectionFields
                                                                    ...ImageElementFields
                                                                    ...ImageElementFieldsOptional
                                                                    ...PlainTextElementFields
                                                                    ...PlainTextElementFieldsOptional
                                                                    ...PlainMultilineTextElementFields
                                                                    ...PlainMultilineTextElementFieldsOptional
                                                                    ...RichTextElementFields
                                                                    ...RichTextElementFieldsOptional
                                                                    ...TableElementFields
                                                                    ...PartitionedTableElementFields
                                                                    ...HighlightsCollectionElementFields
                                                                    ...GovernanceRoleElementFields
                                                                    ...ProductApplicationSegmentCardCollectionElementFields
                                                                    ...BrandCardCollectionElementFields
                                                                    ...CompanyFocusTopicElementFields
                                                                    ...InfoBannerCollectionElementFields
                                                                    ...ElementReferenceFields
                                                                    ...InternalLinkSectionElementFields
                                                                    ... on object_folder {
                                                                        __typename
                                                                        key
                                                                        fullpath
                                                                        # Level 8
                                                                        children {
                                                                            ...ExternalLinkFields
                                                                            ...ExternalLinkFieldsOptional
                                                                            ...InternalLinkFields
                                                                            ...InternalLinkFieldsOptional
                                                                            ...LinkCollectionFields
                                                                            ...ImageLinkCollectionFields
                                                                            ...ImageElementFields
                                                                            ...ImageElementFieldsOptional
                                                                            ...PlainTextElementFields
                                                                            ...PlainTextElementFieldsOptional
                                                                            ...PlainMultilineTextElementFields
                                                                            ...PlainMultilineTextElementFieldsOptional
                                                                            ...RichTextElementFields
                                                                            ...RichTextElementFieldsOptional
                                                                            ...TableElementFields
                                                                            ...PartitionedTableElementFields
                                                                            ...HighlightsCollectionElementFields
                                                                            ...GovernanceRoleElementFields
                                                                            ...ProductApplicationSegmentCardCollectionElementFields
                                                                            ...BrandCardCollectionElementFields
                                                                            ...CompanyFocusTopicElementFields
                                                                            ...InfoBannerCollectionElementFields
                                                                            ...ElementReferenceFields
                                                                            ...InternalLinkSectionElementFields
                                                                            ... on object_folder {
                                                                                __typename
                                                                                key
                                                                                fullpath
                                                                                # Level 9
                                                                                children {
                                                                                    ...ExternalLinkFields
                                                                                    ...ExternalLinkFieldsOptional
                                                                                    ...InternalLinkFields
                                                                                    ...InternalLinkFieldsOptional
                                                                                    ...LinkCollectionFields
                                                                                    ...ImageLinkCollectionFields
                                                                                    ...ImageElementFields
                                                                                    ...ImageElementFieldsOptional
                                                                                    ...PlainTextElementFields
                                                                                    ...PlainTextElementFieldsOptional
                                                                                    ...PlainMultilineTextElementFields
                                                                                    ...PlainMultilineTextElementFieldsOptional
                                                                                    ...RichTextElementFields
                                                                                    ...RichTextElementFieldsOptional
                                                                                    ...TableElementFields
                                                                                    ...PartitionedTableElementFields
                                                                                    ...HighlightsCollectionElementFields
                                                                                    ...GovernanceRoleElementFields
                                                                                    ...ProductApplicationSegmentCardCollectionElementFields
                                                                                    ...BrandCardCollectionElementFields
                                                                                    ...CompanyFocusTopicElementFields
                                                                                    ...InfoBannerCollectionElementFields
                                                                                    ...ElementReferenceFields
                                                                                    ...InternalLinkSectionElementFields
                                                                                    ... on object_folder {
                                                                                        __typename
                                                                                        key
                                                                                        fullpath
                                                                                        # Level 10
                                                                                        children {
                                                                                            ...ExternalLinkFields
                                                                                            ...ExternalLinkFieldsOptional
                                                                                            ...InternalLinkFields
                                                                                            ...InternalLinkFieldsOptional
                                                                                            ...LinkCollectionFields
                                                                                            ...ImageLinkCollectionFields
                                                                                            ...ImageElementFields
                                                                                            ...ImageElementFieldsOptional
                                                                                            ...PlainTextElementFields
                                                                                            ...PlainTextElementFieldsOptional
                                                                                            ...PlainMultilineTextElementFields
                                                                                            ...PlainMultilineTextElementFieldsOptional
                                                                                            ...RichTextElementFields
                                                                                            ...RichTextElementFieldsOptional
                                                                                            ...TableElementFields
                                                                                            ...PartitionedTableElementFields
                                                                                            ...HighlightsCollectionElementFields
                                                                                            ...GovernanceRoleElementFields
                                                                                            ...ProductApplicationSegmentCardCollectionElementFields
                                                                                            ...BrandCardCollectionElementFields
                                                                                            ...CompanyFocusTopicElementFields
                                                                                            ...InfoBannerCollectionElementFields
                                                                                            ...ElementReferenceFields
                                                                                            ...InternalLinkSectionElementFields
                                                                                        }
                                                                                    }
                                                                                }
                                                                            }
                                                                        }
                                                                    }
                                                                }
                                                            }
                                                        }
                                                    }
                                                }
                                            }
                                        }
                                    }
                                }
                            }
                        }
                    }
                }
            }
        }
    `,
    'GET_SITE_CONTENT_ELEMENT_LISTING_QUERY'
);

export function getSiteContentElementListingQueryVariables(
    localeCode: LocaleCode,
    fullpath?: string
) {
    return {
        defaultLanguage: LOCALE_BY_CODE[localeCode].pimcoreCode,
        fullpath: fullpath ? `/Site Content/${fullpath}` : '/Site Content',
    };
}
